import * as React from "react";
import { Phone, WhatsappLogo, EnvelopeSimple, Plus, ArrowLeft, Crown } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { CustomerDetailLabels, CustomerProfile } from "./types";
import { getInitials, formatDateShort } from "./utils";

interface CustomerHeaderProps {
  profile: CustomerProfile;
  labels: CustomerDetailLabels;
  onBack?: () => void;
  onCall?: () => void;
  onWhatsapp?: () => void;
  onEmail?: () => void;
  onNewAppointment?: () => void;
}

const tierStyles: Record<string, string> = {
  bronze: "bg-[hsl(25_70%_45%/0.15)] text-[hsl(25_70%_35%)] dark:text-[hsl(25_70%_70%)] border-[hsl(25_70%_45%/0.3)]",
  silver: "bg-biz-muted text-biz-foreground-muted border-biz-border",
  gold: "bg-biz-accent-subtle text-biz-accent border-biz-accent/30",
  platinum:
    "bg-gradient-to-r from-biz-primary-subtle to-biz-accent-subtle text-biz-primary border-biz-primary/30",
};

export function CustomerHeader({
  profile,
  labels,
  onBack,
  onCall,
  onWhatsapp,
  onEmail,
  onNewAppointment,
}: CustomerHeaderProps) {
  const { customer, pets, stats } = profile;
  const tier = customer.loyaltyTier;

  return (
    <header className="sticky top-0 z-20 bg-biz-surface/95 backdrop-blur border-b border-biz-border">
      <div className="px-6 py-5">
        <div className="flex items-start gap-5">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label={labels.backToList}
              className="h-11 w-11 -ml-2 mt-1 inline-flex items-center justify-center rounded-biz-btn text-biz-foreground-muted hover:bg-biz-muted biz-focus-ring"
            >
              <ArrowLeft size={20} weight="regular" />
            </button>
          )}

          {/* Avatar */}
          <div className="relative shrink-0">
            <div
              className={cn(
                "h-20 w-20 rounded-full overflow-hidden flex items-center justify-center text-2xl font-display font-bold",
                "bg-biz-primary-subtle text-biz-primary-subtle-fg ring-2 ring-biz-border",
              )}
              aria-hidden={!customer.avatarUrl}
            >
              {customer.avatarUrl ? (
                <img src={customer.avatarUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <span>{getInitials(customer.fullName)}</span>
              )}
            </div>
            {customer.isVip && (
              <span
                className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-biz-accent text-biz-accent-foreground flex items-center justify-center ring-2 ring-biz-surface"
                aria-label={labels.vipLabel}
                title={labels.vipLabel}
              >
                <Crown size={16} weight="regular" />
              </span>
            )}
          </div>

          {/* Identity */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-display font-bold tracking-tight text-biz-foreground truncate">
                {customer.fullName}
              </h1>
              {tier && (
                <span
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide",
                    tierStyles[tier],
                  )}
                >
                  {labels.loyaltyTier[tier]}
                  {typeof customer.loyaltyPoints === "number" && (
                    <span className="font-mono-num font-medium opacity-80">
                      · {labels.loyaltyPoints(customer.loyaltyPoints)}
                    </span>
                  )}
                </span>
              )}
            </div>

            <div className="mt-1.5 flex items-center gap-x-5 gap-y-1 flex-wrap text-sm text-biz-foreground-muted">
              <a href={`tel:${customer.phone}`} className="font-mono-num hover:text-biz-primary biz-focus-ring rounded">
                {customer.phone}
              </a>
              {customer.email && (
                <a href={`mailto:${customer.email}`} className="hover:text-biz-primary biz-focus-ring rounded truncate">
                  {customer.email}
                </a>
              )}
              <span>· {pets.length} {labels.tabs.pets.toLowerCase()}</span>
              {stats.lastVisitAt && (
                <span className="font-mono-num">
                  · {labels.overview.lastVisit(formatDateShort(stats.lastVisitAt))}
                </span>
              )}
            </div>

            {customer.tags && customer.tags.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {customer.tags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center rounded-full bg-biz-muted text-biz-foreground-muted px-2.5 py-0.5 text-xs font-medium"
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            <ActionIcon label={labels.contact.call} onClick={onCall} icon={<Phone size={20} weight="regular" />} />
            <ActionIcon
              label={labels.contact.whatsapp}
              onClick={onWhatsapp}
              icon={<WhatsappLogo size={20} weight="regular" />}
              tone="success"
            />
            <ActionIcon
              label={labels.contact.email}
              onClick={onEmail}
              icon={<EnvelopeSimple size={20} weight="regular" />}
              disabled={!customer.email}
            />
            <button
              type="button"
              onClick={onNewAppointment}
              className="ml-2 h-16 px-5 rounded-biz-btn bg-biz-primary hover:bg-biz-primary-hover text-biz-primary-foreground font-semibold text-sm inline-flex items-center gap-2 biz-focus-ring transition-colors shadow-biz-sm"
            >
              <Plus size={20} weight="regular" />
              {labels.overview.newAppointment}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

function ActionIcon({
  label,
  icon,
  onClick,
  tone,
  disabled,
}: {
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  tone?: "success";
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        "h-14 w-14 inline-flex items-center justify-center rounded-biz-btn border border-biz-border bg-biz-surface text-biz-foreground-muted",
        "hover:bg-biz-muted hover:text-biz-foreground transition-colors biz-focus-ring",
        "disabled:opacity-40 disabled:cursor-not-allowed",
        tone === "success" && "hover:text-biz-success hover:border-biz-success/40",
      )}
    >
      {icon}
    </button>
  );
}
